/**
 * WindGlRenderer — WebGL renderer for the wind heatmap.
 *
 * SpecRef: ARCH-002
 * Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md
 *
 * Replaces the worker-based Canvas2D pipeline with a single-pass
 * fragment shader that performs orthographic inverse projection on the GPU.
 *
 * Usage:
 *   const renderer = new WindGlRenderer(canvas);
 *   renderer.uploadWindTexture(0, windData, gridWidth, gridHeight);
 *   renderer.setColorRamp(rampData);
 *   renderer.setProjection(rotate, scale, translate);
 *   renderer.setForecastHour(0);
 *   renderer.render();
 *   // ...
 *   renderer.dispose();
 */
// eslint-disable-next-line import-x/no-internal-modules
import { MAX_WIND } from '../model/constants';

import { FRAGMENT_SHADER_SOURCE, VERTEX_SHADER_SOURCE } from './shaders';
import { buildColorRamp } from './windGl';

// eslint-disable-next-line import-x/no-internal-modules
import type { WindColorTheme } from '../model/types';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProjectionState {
  rotate: [number, number, number];
  scale: number;
  translate: [number, number];
}

interface ForecastTexture {
  texture: WebGLTexture;
  width: number;
  height: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const compileShader = (
  gl: WebGLRenderingContext,
  type: number,
  source: string,
): WebGLShader => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error('Failed to create shader');
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }
  return shader;
};

const createProgram = (
  gl: WebGLRenderingContext,
  vertSrc: string,
  fragSrc: string,
): WebGLProgram => {
  const vert = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
  const frag = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);

  const program = gl.createProgram();
  if (!program) {
    throw new Error('Failed to create program');
  }

  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link error: ${info}`);
  }

  // Shaders are linked — safe to delete individual objects
  gl.deleteShader(vert);
  gl.deleteShader(frag);

  return program;
};

// ── Renderer ──────────────────────────────────────────────────────────────────

export class WindGlRenderer {
  private readonly gl: WebGLRenderingContext;

  private program: WebGLProgram;

  private quadBuffer: WebGLBuffer;

  private disposed = false;

  // Uniform locations (cached for hot path)
  private locTranslate: WebGLUniformLocation | null = null;

  private locScale: WebGLUniformLocation | null = null;

  private locRotate: WebGLUniformLocation | null = null;

  private locResolution: WebGLUniformLocation | null = null;

  private locWindTex: WebGLUniformLocation | null = null;

  private locColorRamp: WebGLUniformLocation | null = null;

  private locWindTexNext: WebGLUniformLocation | null = null;

  private locBlend: WebGLUniformLocation | null = null;

  // Texture management
  private colorRampTexture: WebGLTexture | null = null;

  private readonly forecastTextures = new Map<number, ForecastTexture>();

  private activeForecast = -1;

  private nextForecast = -1;

  private blendFactor = 0;

  // Context‐loss handling
  private _contextLost = false;

  // Active color theme for ramp generation
  private activeTheme?: WindColorTheme;

  // Global opacity multiplier [0..1]
  private activeOpacity = 1;

  // Opacity threshold [0..1] — wind speeds below this fraction are transparent
  private activeThreshold = 0;

  // Maximum wind speed for color ramp encoding
  private activeMaxWind = MAX_WIND;

  private _contextRestoreCount = 0;

  private _onContextRestored: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl', {
      alpha: true,
      // premultipliedAlpha:true — must match the fragment shader which outputs
      // premultiplied RGBA (color.rgb * color.a, color.a).  With straight alpha
      // (false) Safari's CSS filter:blur() spreads colour from transparent
      // pixels (alpha=0 but non-zero RGB) across the globe because it composites
      // the canvas as if it were already premultiplied.  Setting true here tells
      // the browser the framebuffer is premultiplied so transparent pixels are
      // truly (0,0,0,0) and the blur kernel has nothing to spread.
      premultipliedAlpha: true,
      antialias: false,
      preserveDrawingBuffer: false,
    });
    if (!gl) {
      throw new Error('WebGL not supported');
    }

    this.gl = gl;

    // Listen for context loss / restore
    canvas.addEventListener('webglcontextlost', this.handleContextLost);
    canvas.addEventListener('webglcontextrestored', this.handleContextRestored);

    // Build program
    this.program = createProgram(
      gl,
      VERTEX_SHADER_SOURCE,
      FRAGMENT_SHADER_SOURCE,
    );
    this.cacheUniformLocations();

    // Full-viewport quad (two triangles in clip space)
    this.quadBuffer = this.createQuadBuffer();

    // Upload the default colour ramp
    this.uploadColorRamp(
      buildColorRamp(
        this.activeTheme,
        this.activeOpacity,
        this.activeThreshold,
        this.activeMaxWind,
      ),
    );

    // No blending needed — single full-screen quad; fragment shader
    // writes correct alpha per pixel (0 outside globe, ramp opacity inside).
    // Enabling BLEND would double-apply alpha against the cleared framebuffer.
  }

  // ── Public API ──────────────────────────────────────────────────────────

  /** Whether the WebGL context has been lost. */
  get contextLost(): boolean {
    return this._contextLost;
  }

  /** Counter incremented on each context restore — lets callers detect recovery. */
  get contextRestoreCount(): number {
    return this._contextRestoreCount;
  }

  /** Register a callback invoked after the WebGL context is restored. */
  set onContextRestored(cb: (() => void) | null) {
    this._onContextRestored = cb;
  }

  /**
   * Reconcile the cached `_contextLost` flag with the real GL state.
   * Browsers may restore a WebGL context without firing `webglcontextrestored`
   * (especially after prolonged background). This method detects that case and
   * runs the full recovery path.
   *
   * @returns `true` if the context was silently restored and recovery ran.
   */
  reconcileContextState(): boolean {
    if (this.disposed || !this._contextLost) {
      return false;
    }
    if (this.gl.isContextLost()) {
      return false;
    }
    // Context was restored but we missed the event — manual recovery
    this.handleContextRestored();
    return true;
  }

  /**
   * Upload wind-speed data for a forecast hour as a LUMINANCE texture.
   * `data` must be a Uint8Array of size `width × height`.
   */
  uploadWindTexture(
    forecastIndex: number,
    data: Uint8Array,
    width: number,
    height: number,
  ): void {
    if (this.disposed || this._contextLost) {
      return;
    }

    const { gl } = this;

    // Reuse or create texture
    let entry = this.forecastTextures.get(forecastIndex);
    if (!entry) {
      const texture = gl.createTexture();
      if (!texture) {
        return;
      }
      entry = { texture, width, height };
      this.forecastTextures.set(forecastIndex, entry);
    }

    gl.bindTexture(gl.TEXTURE_2D, entry.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.LUMINANCE,
      width,
      height,
      0,
      gl.LUMINANCE,
      gl.UNSIGNED_BYTE,
      data,
    );
    // Nearest-neighbour for crisp lookup; CSS blur(4px) handles antialiasing
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    entry.width = width;
    entry.height = height;
  }

  /**
   * Upload an ImageBitmap (decoded from PNG) as a LUMINANCE texture.
   * The browser decodes grayscale PNG to RGBA; WebGL extracts the R channel
   * when uploading with LUMINANCE internal format.
   *
   * SpecRef: ARCH-002
   * Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md#windglrendereruploadwindtexturefrombitmap
   */
  uploadWindTextureFromBitmap(
    forecastIndex: number,
    bitmap: ImageBitmap,
    width: number,
    height: number,
  ): void {
    if (this.disposed || this._contextLost) {
      return;
    }

    const { gl } = this;

    let entry = this.forecastTextures.get(forecastIndex);
    if (!entry) {
      const texture = gl.createTexture();
      if (!texture) {
        return;
      }
      entry = { texture, width, height };
      this.forecastTextures.set(forecastIndex, entry);
    }

    gl.bindTexture(gl.TEXTURE_2D, entry.texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.LUMINANCE,
      gl.LUMINANCE,
      gl.UNSIGNED_BYTE,
      bitmap,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    entry.width = width;
    entry.height = height;
  }

  /**
   * Replace the default 1D colour-ramp texture.
   * `ramp` is a Uint8Array of length 1024 (256 RGBA entries).
   */
  setColorRamp(ramp: Uint8Array): void {
    this.uploadColorRamp(ramp);
  }

  /**
   * Switch the active color theme.
   * Rebuilds and re-uploads the 256×1 RGBA colour-ramp texture.
   */
  setTheme(theme: WindColorTheme): void {
    this.activeTheme = theme;
    this.uploadColorRamp(
      buildColorRamp(
        theme,
        this.activeOpacity,
        this.activeThreshold,
        this.activeMaxWind,
      ),
    );
  }

  /**
   * Update the global opacity multiplier [0..1].
   * Rebuilds and re-uploads the colour-ramp texture.
   */
  setOpacity(opacity: number): void {
    this.activeOpacity = Math.max(0, Math.min(1, opacity));
    this.uploadColorRamp(
      buildColorRamp(
        this.activeTheme,
        this.activeOpacity,
        this.activeThreshold,
        this.activeMaxWind,
      ),
    );
  }

  /**
   * Update the opacity threshold [0..1].
   * Wind speeds below `threshold * MAX_WIND` become fully transparent.
   * Rebuilds and re-uploads the colour-ramp texture.
   */
  setThreshold(threshold: number): void {
    this.activeThreshold = Math.max(0, Math.min(1, threshold));
    this.uploadColorRamp(
      buildColorRamp(
        this.activeTheme,
        this.activeOpacity,
        this.activeThreshold,
        this.activeMaxWind,
      ),
    );
  }

  /**
   * Update the maximum wind speed used for color ramp encoding.
   * Rebuilds and re-uploads the colour-ramp texture.
   */
  setMaxWind(maxWind: number): void {
    this.activeMaxWind = maxWind;
    this.uploadColorRamp(
      buildColorRamp(
        this.activeTheme,
        this.activeOpacity,
        this.activeThreshold,
        this.activeMaxWind,
      ),
    );
  }

  /** Select which forecast-hour wind texture is active for rendering. */
  setForecastHour(index: number): void {
    this.activeForecast = index;
    this.nextForecast = index;
    this.blendFactor = 0;
  }

  /**
   * Set up dual-texture blending between two forecast hours.
   * `factor` interpolates linearly: 0.0 = show currentIndex, 1.0 = show nextIndex.
   */
  setBlend(currentIndex: number, nextIndex: number, factor: number): void {
    this.activeForecast = currentIndex;
    this.nextForecast = nextIndex;
    this.blendFactor = factor;
  }

  /**
   * Update projection uniforms from D3 geoOrthographic state.
   */
  setProjection(
    rotate: [number, number, number],
    scale: number,
    translate: [number, number],
  ): void {
    if (this.disposed || this._contextLost) {
      return;
    }

    const { gl, program } = this;
    gl.useProgram(program);
    gl.uniform2f(this.locTranslate, translate[0], translate[1]);
    gl.uniform1f(this.locScale, scale);
    gl.uniform3f(this.locRotate, rotate[0], rotate[1], rotate[2]);
  }

  /** Resize the canvas backing store to match CSS layout size. */
  resize(width: number, height: number): void {
    if (this.disposed || this._contextLost) {
      return;
    }

    const { gl } = this;
    gl.canvas.width = width;
    gl.canvas.height = height;
    gl.viewport(0, 0, width, height);
  }

  /**
   * Draw the heatmap. Call after setProjection / setForecastHour.
   * Extremely cheap: one draw call with current uniforms.
   */
  render(): void {
    if (this.disposed || this._contextLost) {
      return;
    }

    const { gl, program } = this;
    const entry = this.forecastTextures.get(this.activeForecast);
    const nextEntry = this.forecastTextures.get(this.nextForecast);

    // Guard: skip draw when no forecast texture is loaded — prevents
    // rendering opaque black from the default (0,0,0,0) texture.
    if (!entry) {
      return;
    }

    gl.useProgram(program);

    // Resolution uniform
    gl.uniform2f(this.locResolution, gl.canvas.width, gl.canvas.height);

    // Blend factor uniform
    gl.uniform1f(this.locBlend, this.blendFactor);

    // Bind wind data texture to unit 0
    gl.activeTexture(gl.TEXTURE0);
    if (entry) {
      gl.bindTexture(gl.TEXTURE_2D, entry.texture);
    }
    gl.uniform1i(this.locWindTex, 0);

    // Bind colour ramp to unit 1
    gl.activeTexture(gl.TEXTURE1);
    if (this.colorRampTexture) {
      gl.bindTexture(gl.TEXTURE_2D, this.colorRampTexture);
    }
    gl.uniform1i(this.locColorRamp, 1);

    // Bind next forecast wind texture to unit 2
    gl.activeTexture(gl.TEXTURE2);
    if (nextEntry) {
      gl.bindTexture(gl.TEXTURE_2D, nextEntry.texture);
    } else if (entry) {
      // Fallback: bind current texture so mix() is a no-op
      gl.bindTexture(gl.TEXTURE_2D, entry.texture);
    }
    gl.uniform1i(this.locWindTexNext, 2);

    // Draw the full-viewport quad
    const posLoc = gl.getAttribLocation(program, 'a_position');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /** Release all GPU resources. */
  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;

    const { gl } = this;
    const canvas = gl.canvas as HTMLCanvasElement;
    canvas.removeEventListener('webglcontextlost', this.handleContextLost);
    canvas.removeEventListener(
      'webglcontextrestored',
      this.handleContextRestored,
    );

    // Delete forecast textures
    for (const { texture } of this.forecastTextures.values()) {
      gl.deleteTexture(texture);
    }
    this.forecastTextures.clear();

    // Delete colour ramp
    if (this.colorRampTexture) {
      gl.deleteTexture(this.colorRampTexture);
      this.colorRampTexture = null;
    }

    gl.deleteBuffer(this.quadBuffer);
    gl.deleteProgram(this.program);
  }

  /** Check if a forecast hour's texture has been uploaded. */
  hasForecastTexture(index: number): boolean {
    return this.forecastTextures.has(index);
  }

  /** Delete a specific forecast texture to free GPU memory. */
  deleteForecastTexture(index: number): void {
    const entry = this.forecastTextures.get(index);
    if (entry) {
      this.gl.deleteTexture(entry.texture);
      this.forecastTextures.delete(index);
    }
  }

  /** Number of forecast textures currently on GPU. */
  get forecastTextureCount(): number {
    return this.forecastTextures.size;
  }

  /**
   * Probe the framebuffer for non-zero alpha to detect a visually blank canvas.
   * Renders one frame, then samples a sparse grid with `gl.readPixels()`.
   * Returns the sum of alpha bytes across the grid; 0 means the canvas is
   * entirely transparent or black (i.e. nothing visible).
   *
   * Safe to call with `preserveDrawingBuffer: false` because `readPixels()`
   * executes synchronously in the same JS task as the preceding `drawArrays()`,
   * before the browser clears the back-buffer at composite time.
   */
  probeAlpha(gridSize = 5): number {
    if (this.disposed || this._contextLost) {
      return -1;
    }

    const { gl } = this;
    const w = gl.canvas.width;
    const h = gl.canvas.height;
    if (w <= 0 || h <= 0) {
      return 0;
    }

    // Re-render so the back-buffer is fresh
    this.render();

    const pixel = new Uint8Array(4);
    let sum = 0;
    for (let row = 0; row < gridSize; row++) {
      const y = Math.floor((h * (row + 1)) / (gridSize + 1));
      for (let col = 0; col < gridSize; col++) {
        const x = Math.floor((w * (col + 1)) / (gridSize + 1));
        gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
        sum += pixel[3]; // alpha channel
      }
    }
    return sum;
  }

  // ── Private ─────────────────────────────────────────────────────────────

  private cacheUniformLocations(): void {
    const { gl, program } = this;
    gl.useProgram(program);
    this.locTranslate = gl.getUniformLocation(program, 'u_translate');
    this.locScale = gl.getUniformLocation(program, 'u_scale');
    this.locRotate = gl.getUniformLocation(program, 'u_rotate');
    this.locResolution = gl.getUniformLocation(program, 'u_resolution');
    this.locWindTex = gl.getUniformLocation(program, 'u_windTex');
    this.locColorRamp = gl.getUniformLocation(program, 'u_colorRamp');
    this.locWindTexNext = gl.getUniformLocation(program, 'u_windTexNext');
    this.locBlend = gl.getUniformLocation(program, 'u_blend');
  }

  private createQuadBuffer(): WebGLBuffer {
    const { gl } = this;
    const buffer = gl.createBuffer();
    if (!buffer) {
      throw new Error('Failed to create buffer');
    }

    // Full-viewport quad as triangle strip: BL, BR, TL, TR
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    return buffer;
  }

  private uploadColorRamp(ramp: Uint8Array): void {
    if (this.disposed || this._contextLost) {
      return;
    }

    const { gl } = this;
    this.colorRampTexture ??= gl.createTexture();

    gl.bindTexture(gl.TEXTURE_2D, this.colorRampTexture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      256,
      1,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      ramp,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  private handleContextLost = (e: Event): void => {
    e.preventDefault();
    this._contextLost = true;
  };

  private readonly handleContextRestored = (): void => {
    this._contextLost = false;
    try {
      // Re-initialise GPU state
      this.program = createProgram(
        this.gl,
        VERTEX_SHADER_SOURCE,
        FRAGMENT_SHADER_SOURCE,
      );
      this.cacheUniformLocations();
      this.quadBuffer = this.createQuadBuffer();
      this.uploadColorRamp(
        buildColorRamp(
          this.activeTheme,
          this.activeOpacity,
          this.activeThreshold,
          this.activeMaxWind,
        ),
      );
    } catch {
      // Recovery failed — mark context as still lost so the watchdog can
      // retry or the component can recreate the renderer.
      this._contextLost = true;
      return;
    }

    // Caller is responsible for re-uploading forecast textures
    this.forecastTextures.clear();
    this.activeForecast = -1;
    this.nextForecast = -1;
    this.blendFactor = 0;
    this._contextRestoreCount += 1;
    this._onContextRestored?.();
  };
}
